import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@staff-app/ui';
import type { IntlAllRequest } from '@staff-app/shared-types';
import { documentsApi } from '@/features/customers/lib/documents-api';
import { useConvertIntlAllQuote, useRaiseIntlAllInvoice } from '../hooks/useIntlAllRequests';

export type AirBillingMode = 'convert' | 'invoice';

/**
 * File the PO in the customer's Document Center and return its id — the same route International
 * Requests' `uploadPoDocument` takes, titled with this tab's AIR number. Undefined when no file.
 */
async function uploadAirPoDocument(
  request: IntlAllRequest,
  poNumber: string,
  file: File | null,
): Promise<string | undefined> {
  if (!file) return undefined;
  const res = await documentsApi.upload(
    {
      accountId: request.accountId ?? undefined,
      stripeId: request.stripeCustomerId,
      docType: 'po',
      title: `Purchase order — ${request.requestNo}`,
      externalNumber: poNumber.trim() || undefined,
      counterpartyName: request.company ?? undefined,
    },
    file,
  );
  return res.document.id;
}

/**
 * Convert the accepted quote, or raise an invoice directly (ROOT + SUPPORT).
 *
 * THE PURCHASE ORDER IS OPTIONAL (Paul, 2026-10-27): a number printed on the invoice and/or a
 * document filed in the Document Center and linked from the invoice. Neither is required, and
 * leaving both blank raises the invoice exactly as before. The document is filed FIRST, so a failed
 * upload stops before anything is billed.
 */
export function AirBillingDialog({
  request,
  mode,
  open,
  onOpenChange,
}: {
  request: IntlAllRequest;
  mode: AirBillingMode;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [poNumber, setPoNumber] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [convert, convertState] = useConvertIntlAllQuote();
  const [raise, raiseState] = useRaiseIntlAllInvoice();
  const { loading, error } = mode === 'convert' ? convertState : raiseState;
  const busy = loading || uploading;

  const submit = async () => {
    if (busy) return;
    setUploadError(null);
    let poDocumentId: string | undefined;
    if (file) {
      setUploading(true);
      try {
        poDocumentId = await uploadAirPoDocument(request, poNumber, file);
      } catch (e) {
        setUploadError(`The purchase order could not be filed: ${(e as Error).message}. Nothing has been billed.`);
        setUploading(false);
        return;
      }
      setUploading(false);
    }
    const input = {
      requestId: request.id,
      poNumber: poNumber.trim() || undefined,
      poDocumentId,
    };
    try {
      if (mode === 'convert') await convert({ variables: { input } });
      else await raise({ variables: { input } });
      onOpenChange(false);
    } catch {
      // Surfaced from `error` below.
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="air-billing-dialog">
        <DialogHeader>
          <DialogTitle>
            {mode === 'convert'
              ? `Convert the quote on ${request.requestNo}`
              : `Raise the invoice for ${request.requestNo}`}
          </DialogTitle>
          <DialogDescription>
            {mode === 'convert'
              ? 'Marks the quote accepted and turns it into an invoice for exactly what it quoted.'
              : 'Bills the routed request: one line per device, and one Freight line.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs font-medium text-muted-foreground">{'Purchase order number'}</Label>
            <Input
              className="mt-1"
              value={poNumber}
              disabled={busy}
              onChange={(e) => setPoNumber(e.target.value)}
              placeholder="4500123456"
              data-testid="air-po-number"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              {'Printed on the invoice the customer receives. Optional.'}
            </p>
          </div>
          <div>
            <Label className="text-xs font-medium text-muted-foreground">{'Purchase order document'}</Label>
            <Input
              className="mt-1"
              type="file"
              accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
              disabled={busy}
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              data-testid="air-po-file"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              {'Filed in this customer’s Document Center and linked from the invoice in Stripe. Optional.'}
            </p>
          </div>
          {uploadError ? (
            <p className="text-sm text-destructive" data-testid="air-po-upload-error">
              {uploadError}
            </p>
          ) : null}
          {error ? (
            <p className="text-sm text-destructive" data-testid="air-billing-error">
              {error.message}
            </p>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            {'Cancel'}
          </Button>
          <Button onClick={submit} disabled={busy} data-testid="air-billing-submit">
            {busy ? 'Working…' : mode === 'convert' ? 'Convert to invoice' : 'Raise invoice'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
